import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ensureNamespaceAsync } from '../../i18n/ensurePageI18n';

import seriesCBImg from './img/seriesCBImg.png';
import seriesCVImg from './img/seriesCVImg.png';
import features from './img/features.png';
import prothermLogo from './img/prothermLogo.png';
import tube1 from './img/tube-1.png';
import tube2 from './img/tube-2.png';
import tube3 from './img/tube-3.png';
import tube4 from './img/tube-4.png';
import spectrumDiagram from './img/spectrumDiagram.png';
import filters from './img/filters.png';
import application from './img/application.png';

// 修正 zh 結構，bundles 以 infrared 命名空間使用
const bundles = {
  en: {
    page1: {
      title_main: 'Process Thermal Dynamics',
      title_sub: 'Electric Infrared Heating Equipment',
      series_title: 'PROTHERM® Series CB & CV Heaters',
      desc_banner: 'Highly efficient long-wave to medium-wave infrared panel heaters, suitable for various industrial heating applications that require multiple watt densities, excellent uniformity, and cleaner heating surfaces. Especially suitable for the plastic and PCB industries.',
      features_main: [
        'The surface temperature of the CB heater can reach up to 1350°F (peak wavelength around 2.0 microns).',
        'The surface temperature of the CV heater can reach up to 1000°F (peak wavelength around 3.0 microns).',
        'Durable black or vitreous enamel steel heating surfaces that resist mechanical damage.',
        'Features a unique internal insulation design that minimizes heat loss from the back.',
        'Reaches full operating temperature in just 3-5 minutes.',
        'Controlled heating/cooling process takes approximately 5-10 minutes.',
        'Provides the same heating effect for all colors.',
        'Suitable for surfaces of all colors.',
        'Customizable according to customer specifications, including size, watt density, and voltage.'
      ],
      series_features_title: 'Series CB & CV Series Features',
      std_features_title: 'Standard Design Features',
      std_features_list: [
        'Aluminized steel metal casing',
        'Customizable sizes',
        'Installation studs',
        'Dimensions (customizable):',
        'Maximum width: 24 inches',
        'Maximum length: 6 feet (depending on width)',
        'Standard thickness: 3 inches'
      ],
      custom_features_title: 'Custom Features',
      custom_features_list: [
        'Stainless steel casing',
        'Thermocouple protection tube (Thermowell)',
        'K-type or J-type thermocouple',
        'Extended leads',
        'Special conduit accessories',
        'Flexible metal conduit',
        'Special installation devices or installation locations',
        'Waterproof design',
        'Minimum thickness 2 inches',
        'Multi-zone heating'
      ]
    },
    page2: {
      title_main: 'Electrothermal Infrared Heating System',
      title_sub: 'High-Efficiency, Energy-Saving, and Flexible Heating Solutions',
      benefits: [
        'Direct Heating: Heats objects directly without warming the surrounding air, minimizing energy loss.',
        'High Thermal Efficiency: Faster heating process reduces production time.',
        'Precision Temperature Control: Multiple heating zones can be customized to target specific areas.',
        'Eco-Friendly Operation: No exhaust emissions, no wasted heat, and lower operating costs.',
        'Rapid Start-Up & Cooling: Reduces standby energy consumption and enhances production efficiency.',
        'Fully Electric Control: No need for gas, ensuring safety and environmental sustainability.',
        'Seamless Integration: Compatible with various production lines and conveyor systems.',
        'Modular Design: Scalable and adjustable according to production demands.',
        'Versatile Applications: Suitable for multiple materials and processes, including metal, plastics, printing, electronics, and automotive manufacturing.'
      ],
      table: {
        headers: ['Heating Methods', 'Heating Speed', 'Energy Consumption', 'Controllability', 'Installation & Maintenance'],
        row1: ['Electric Infrared Heating', 'Ultra-Fast (Second-Level Heating)', 'Low (no additional heat loss)', 'High (0-100% adjustable)', 'Simple (ready to use)'],
        row2: ['Convection Hot Air Heating', 'Slow (Requires Air Heating)', 'High (large heat loss)', 'Low (uneven heat conduction)', 'Complex (air duct design limitations)'],
        row3: ['Gas Heating', 'Medium', 'High (requires combustion of gas)', 'Medium (difficult to control precisely)', 'Complex (requires exhaust system)']
      },
      diagram_labels: ['TUBE', 'TUBE WITH BOARD', 'TUBE WITH REFLECTOR', 'FOIL ON BOARD'],
      legend: ['DIRECT ENERGY', 'RE-DIRECT ENERGY'],
      app_captions: ['Applications | Practical Applications of Infrared Heating Industry', 'Filters | Application of Infrared Heating in Filter Manufacturing']
    }
  },
  zh: {
    page1: {
      title_main: 'Process Thermal Dynamics',
      title_sub: '電熱紅外線加熱設備',
      series_title: 'PROTHERM® 系列 CB & CV 加熱器',
      desc_banner: '高效的長波至中波紅外線平板加熱器，適用於各類需要多種瓦特密度、優異均勻性以及更潔淨發熱表面的工業加熱應用。特別適用於塑膠領域及PCB領域。',
      features_main: [
        'CB 加熱器的表面溫度可高達 1350°F (峰值波長約 2.0 微米)',
        'CV 加熱器的表面溫度可高達 1000°F (峰值波長約 3.0 微米)',
        '堅固的黑色或玻璃搪瓷 (vitreous enamel) 鋼製發熱表面，能抵抗機械損傷',
        '採用獨特的內部絕緣設計，將背部熱損失降到最低',
        '僅需 3-5 分鐘即可達到完整工作溫度',
        '受控的加熱/冷卻過程約需 5-10 分鐘',
        '對所有顏色均可達到相同的加熱效果',
        '適用於所有顏色的表面',
        '可依照客戶規格製造，可客製尺寸、瓦特密度及電壓'
      ],
      series_features_title: 'Series CB & CV 系列特點',
      std_features_title: '標準設計特點',
      std_features_list: [
        '鋁化鋼板金屬外殼',
        '可定製尺寸',
        '安裝螺柱',
        '尺寸 (可定製)：',
        '最大寬度：24 英寸',
        '最大長度：6 英尺 (取決於寬度)',
        '標準厚度：3 英寸'
      ],
      custom_features_title: '客製功能',
      custom_features_list: [
        '不鏽鋼外殼',
        '熱電偶保護管 (Thermowell)',
        'K型 或 J型熱電偶',
        '加長導線',
        '特殊導管配件',
        '柔性金屬導管',
        '特殊安裝裝置或安裝位置',
        '防水設計',
        '最小厚度 2 英寸',
        '多區域加熱'
      ]
    },
    page2: {
      title_main: '電熱紅外線加熱系統',
      title_sub: '—— 高效、節能、靈活的加熱解決方案',
      benefits: [
        '直接加熱物體，不加熱空氣，能量損耗低',
        '熱轉換效率高，加熱更快，減少生產時間',
        '可根據需求設置多個溫控區域，精準加熱不同部位',
        '無需排放廢氣，無熱能浪費，降低運行成本',
        '快速啟動與冷卻，減少待機能耗，提高生產效率',
        '全電控制，無需燃氣，安全環保',
        '適用於各種生產線與設備，可與輸送系統無縫整合',
        '模組化設計，可隨產能需求擴展與調整',
        '適用於多種材料與工藝，從金屬、塑膠、印刷、電子到汽車製造'
      ],
      table: {
        headers: ['加熱方式', '加熱速度', '能耗', '可控性', '安裝與維護'],
        row1: ['電熱紅外線', '⚡ 超快 (秒級加熱)', '🔥 低 (無額外熱能損耗)', '🎯 高 (0-100%可調)', '🔧 簡單 (即裝即用)'],
        row2: ['對流式熱風', '慢 (需加熱空氣)', '高 (熱能損耗大)', '低 (熱傳導不均勻)', '複雜 (風道設計限制)'],
        row3: ['煤氣式加熱', '中等', '高 (需燃燒氣體)', '中 (難以精細控制)', '複雜 (需排氣裝置)']
      },
      diagram_labels: ['TUBE', 'TUBE WITH BOARD', 'TUBE WITH REFLECTOR', 'FOIL ON BOARD'],
      legend: ['DIRECT ENERGY', 'RE-DIRECT ENERGY'],
      app_captions: ['Applications | 紅外線加熱產業實際應用', 'Filters | 紅外線加熱在過濾器製造中的應用']
    }
  }
};

// safeList: 保證回傳陣列，避免 t(...) 不是陣列時 .map 錯誤
const safeList = (tFunc, key) => {
  const raw = tFunc(key, { returnObjects: true });
  if (Array.isArray(raw)) return raw;
  if (raw == null) return [];
  if (typeof raw === 'object') return Object.values(raw);
  return [raw];
};

const Infrared = () => {
  const { t, i18n } = useTranslation('infrared');
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let mounted = true;
    ensureNamespaceAsync(i18n, 'infrared', bundles).then(() => { if (mounted) setReady(true); });
    return () => { mounted = false; };
  }, [i18n]);
  if (!ready) {
    return <div className="min-h-screen flex items-center justify-center bg-black text-gray-300">Loading…</div>;
  }
  const currentLang = i18n.language || 'zh';
  const placeholderImg = './img/zh.jpg';
  const tubeImages = [tube1, tube2, tube3, tube4];

  return (
    <div className="min-h-screen flex flex-col items-center p-4 md:p-8 font-sans bg-gradient-to-br from-gray-950 via-gray-900 to-black text-white">
      <div className="w-full max-w-6xl relative mb-8">
        <Link to="/" className="inline-block px-5 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-white font-medium transition shadow-lg">← {currentLang.startsWith('zh') ? '返回' : 'Back'}</Link>
      </div>
      {/* Revert inner wrapper to light theme */}
      <div className="max-w-6xl w-full bg-white shadow-2xl rounded-xl overflow-hidden text-gray-800">
        {/* PAGE 1 */}
        <section className="p-6 md:p-12 border-b-8 border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 border-b pb-4">
            <img src={prothermLogo}  alt="Protherm Logo"  />

            <div className="text-left md:text-right">
              <h2 className="text-xl md:text-2xl font-semibold text-blue-900">{t('infrared:page1.title_main')}</h2>
              <h3 className="text-lg text-gray-700">{t('infrared:page1.title_sub')}</h3>
            </div>
          </div>
          <div className="flex flex-col md:flex-row justify-center gap-8 mb-10">
            <div className="text-center flex-1 max-w-md mx-auto md:mx-0">
              <div className="w-full bg-gray-100 rounded flex items-center justify-center overflow-hidden mb-2 border-gray-300">
                <img src={seriesCBImg} alt="Series CB" className="w-full h-auto object-contain" />
              </div>
              <p className="font-bold text-lg">Series CB</p>
            </div>
            <div className="text-center flex-1 max-w-md mx-auto md:mx-0">
              <div className="w-full bg-gray-100 rounded flex items-center justify-center overflow-hidden mb-2  border-gray-300">
                <img src={seriesCVImg} alt="Series CV" className="w-full h-auto object-contain" />
              </div>
              <p className="font-bold text-lg">Series CV</p>
            </div>
          </div>
          <div className="bg-blue-900 text-white p-6 rounded-lg shadow-md mb-8">
            <h3 className="text-xl font-bold mb-2 text-white">{t('infrared:page1.series_title')}</h3>
            <p className="leading-relaxed opacity-90 text-white">{t('infrared:page1.desc_banner')}</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            <ul className="space-y-2 pl-5 list-disc marker:text-red-600">
              {safeList(t, 'infrared:page1.features_main').map((item, idx) => <li key={idx} className="text-gray-700 font-medium">{item}</li>)}
            </ul>
            <div className="flex items-center justify-center">
              <div className="w-full h-full bg-gray-100 border-gray-300 rounded-lg overflow-hidden shadow-md">
                <img src={features} alt="Features" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
          <h3 className="text-2xl font-bold text-blue-900 mb-6 flex items-center"><span className="w-3 h-3 bg-blue-600 mr-3 inline-block" />{t('infrared:page1.series_features_title')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 bg-gray-100 p-8 rounded-xl">
            <div>
              <h4 className="text-xl font-bold mb-4 border-b-2 border-gray-300 pb-2">{t('infrared:page1.std_features_title')}</h4>
              <ul className="space-y-2 pl-4 list-square">{safeList(t, 'infrared:page1.std_features_list').map((item, idx) => <li key={idx} className="text-gray-700 text-sm md:text-base">▪ {item}</li>)}</ul>
            </div>
            <div>
              <h4 className="text-xl font-bold mb-4 border-b-2 border-gray-300 pb-2">{t('infrared:page1.custom_features_title')}</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">{safeList(t, 'infrared:page1.custom_features_list').map((item, idx) => <li key={idx} className="text-gray-700 text-sm md:text-base list-none">▪ {item}</li>)}</div>
            </div>
          </div>
        </section>
        {/* PAGE 2 */}
        <section className="p-6 md:p-12">
          <div className="border-l-8 border-blue-800 pl-6 mb-8">
            <h2 className="text-3xl font-bold text-blue-900">{t('infrared:page2.title_main')}</h2>
            <p className="text-xl text-gray-600 mt-2 font-medium">{t('infrared:page2.title_sub')}</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-12">
            <ul className="space-y-3">{safeList(t, 'infrared:page2.benefits').map((item, idx) => (<li key={idx} className="flex items-start"><span className="text-red-600 font-bold mr-2 text-lg">•</span><span className="text-gray-700 leading-snug">{item}</span></li>))}</ul>
            <div className="flex flex-col items-center justify-center">
              <div className="w-full bg-gray-100  border-gray-300 rounded flex items-center justify-center mb-2">
                <img src={spectrumDiagram} alt="Spectrum Diagram" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
          <div className="overflow-x-auto mb-12 rounded-lg shadow">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-200 text-gray-800 font-bold">{safeList(t, 'infrared:page2.table.headers').map((h, i) => <th key={i} className="p-4 border-b-2 border-gray-300 min-w-[120px]">{h}</th>)}</tr>
              </thead>
              <tbody className="text-sm md:text-base">
                <tr className="bg-blue-50 hover:bg-blue-100 transition">
                  <td className="p-4 border-b border-gray-200 font-bold  bg-blue-800 text-white">{t('infrared:page2.table.row1.0')}</td>
                  <td className="p-4 border-b border-gray-200">{t('infrared:page2.table.row1.1')}</td>
                  <td className="p-4 border-b border-gray-200">{t('infrared:page2.table.row1.2')}</td>
                  <td className="p-4 border-b border-gray-200">{t('infrared:page2.table.row1.3')}</td>
                  <td className="p-4 border-b border-gray-200">{t('infrared:page2.table.row1.4')}</td>
                </tr>
                <tr className="bg-white hover:bg-gray-50 transition">
                  <td className="p-4 border-b border-gray-200 font-bold  bg-blue-700 text-white opacity-90">{t('infrared:page2.table.row2.0')}</td>
                  <td className="p-4 border-b border-gray-200">{t('infrared:page2.table.row2.1')}</td>
                  <td className="p-4 border-b border-gray-200">{t('infrared:page2.table.row2.2')}</td>
                  <td className="p-4 border-b border-gray-200">{t('infrared:page2.table.row2.3')}</td>
                  <td className="p-4 border-b border-gray-200">{t('infrared:page2.table.row2.4')}</td>
                </tr>
                <tr className="bg-white hover:bg-gray-50 transition">
                  <td className="p-4 border-b border-gray-200 font-bold  bg-blue-600 text-white opacity-80">{t('infrared:page2.table.row3.0')}</td>
                  <td className="p-4 border-b border-gray-200">{t('infrared:page2.table.row3.1')}</td>
                  <td className="p-4 border-b border-gray-200">{t('infrared:page2.table.row3.2')}</td>
                  <td className="p-4 border-b border-gray-200">{t('infrared:page2.table.row3.3')}</td>
                  <td className="p-4 border-b border-gray-200">{t('infrared:page2.table.row3.4')}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 text-center">{safeList(t, 'infrared:page2.diagram_labels').map((label, idx) => (<div key={idx} className="flex flex-col items-center"><div className="w-full aspect-square bg-white border-gray-200 rounded p-2 mb-2 overflow-hidden"><img src={tubeImages[idx]} alt={label} className="w-full h-full object-contain" /></div><span className="text-sm font-bold text-gray-600">{label}</span></div>))}</div>
          <div className="flex justify-center gap-6 mb-12 text-sm font-bold">
            <div className="flex items-center"><span className="w-4 h-4 bg-red-600 mr-2" />{safeList(t, 'infrared:page2.legend')[0]}</div>
            <div className="flex items-center"><span className="w-4 h-4 bg-yellow-400 mr-2" />{safeList(t, 'infrared:page2.legend')[1]}</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="group relative overflow-hidden rounded-lg shadow-lg">
              <div className="aspect-video bg-gray-300 w-full">
                <img src={application} alt="Application 1" className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
              </div>
              <div className="bg-blue-900 text-white p-3 text-center text-sm font-medium">{safeList(t, 'infrared:page2.app_captions')[0]}</div>
            </div>
            <div className="group relative overflow-hidden rounded-lg shadow-lg">
              <div className="aspect-video bg-gray-300 w-full">
                <img src={filters} alt="Application 2" className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
              </div>
              <div className="bg-blue-900 text-white p-3 text-center text-sm font-medium">{safeList(t, 'infrared:page2.app_captions')[1]}</div>
            </div>
          </div>
        </section>
      </div>
      <div className="mt-8 text-gray-300 text-xs">© 2025 Wintriss Engineering. All rights reserved.</div>
    </div>
  );
};

export default Infrared;

